import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { RelatedKnowledgeAuto } from "@/components/site/RelatedKnowledgeAuto";
import { breadcrumbs, faqPage, serviceSchema, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad ingår i manusarbete för film?",
    a: "Brief och målgruppsanalys, kärnidé, struktur (scenlista i bild/ljud-kolumner), speakertext eller dialog, samt revideringar fram till godkänt manus. Storyboard och produktionsplan är separata steg som bygger vidare på manuset.",
  },
  {
    q: "Kan ni skriva manus utan att producera filmen?",
    a: "Ja. Vi tar fristående manus- och konceptuppdrag där ni sedan producerar själva eller med annan partner. Ni får manus, scenlista och en produktionsteknisk kommentar om vad som krävs för att spela in det.",
  },
  {
    q: "Vad kostar manus och koncept?",
    a: "Ett manus till en kortare företagsfilm ligger typiskt på 8 000–20 000 kr. Reklamfilmsmanus med koncept, flera idéhöjder och versionering: 25 000–75 000 kr. Ett kampanjkoncept med plattform och manus för flera filmer: från 60 000 kr.",
  },
  {
    q: "Hur lång tid tar manusarbetet?",
    a: "Räkna med 1–2 veckor från brief till första utkast för en företagsfilm, och 2–4 veckor för ett reklamfilmskoncept med flera idéspår. Godkännandeloopar internt hos er är oftast det som styr tidplanen mest.",
  },
  {
    q: "Vad är skillnaden mellan manus och storyboard?",
    a: "Manuset beskriver vad som sägs och händer. Storyboard (bildmanus) visar hur det ser ut bild för bild — bildutsnitt, kamerarörelse och rytm. Manus kommer först, storyboard är översättningen till bild.",
  },
  {
    q: "Hur många ord blir en minut film?",
    a: "En bra tumregel för svensk speakertext är 130–150 ord per minut. En 60-sekundersfilm rymmer alltså cirka 140 ord — mindre om bilden ska få tala eller om det finns grafik som behöver luft.",
  },
];

const TITLE = "Manus, koncept och filmstrategi | Fenny";
const DESCRIPTION =
  "Manusarbete, konceptutveckling och strategisk planering av reklamfilm och företagsfilm. Fristående manusuppdrag eller som del av produktion. Priser och process 2026.";

export const Route = createFileRoute("/manus-och-koncept")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Manus, koncept och filmstrategi — Fenny" },
      { property: "og:description", content: "Från brief till godkänt manus och storyboard. Strategi och idé innan kameran rullar." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/manus-och-koncept" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/manus-och-koncept" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: "/manus-och-koncept", name: TITLE, description: DESCRIPTION })) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Manus och konceptutveckling för film",
            path: "/manus-och-koncept",
            serviceType: "Manusarbete och konceptutveckling",
            description:
              "Manus, koncept, storyboard och strategisk planering av reklamfilm, företagsfilm och kampanjer.",
            priceRange: "8 000–75 000 kr",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Tjänster", path: "/tjanster" },
            { name: "Manus och koncept", path: "/manus-och-koncept" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <LandingPage
        eyebrow="Manus & koncept"
        title={<>Filmen avgörs innan kameran rullar.</>}
        lede="Manus, koncept och filmstrategi för reklamfilm, företagsfilm och kampanjer. Vi tar er från otydlig brief till godkänt manus, storyboard och en produktionsplan som håller — antingen som fristående uppdrag eller som första steget i en produktion."
        tldr={
          <p>
            Manusarbete innebär att översätta ett affärsmål till en kärnidé,
            struktur och färdig text för film. Pris i Sverige 2026:{" "}
            <strong>8 000–20 000 kr</strong> för manus till en företagsfilm,{" "}
            <strong>25 000–75 000 kr</strong> för reklamfilmsmanus med koncept.
            Tumregel: <strong>130–150 ord speakertext per minut film</strong>.
            Fenny tar fristående manusuppdrag och manus som del av produktion.
          </p>
        }
        ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
        ctaSecondary={{ label: "Se priser", to: "/priser" }}
        trackingId="manus-och-koncept"
        bullets={[
          { title: "Idé före utförande", body: "Vi börjar i målet och målgruppen — inte i formatet eller kameran." },
          { title: "Fristående eller ihop", body: "Köp bara manus och koncept, eller låt oss ta hela produktionen." },
          { title: "Produktionsbart från dag ett", body: "Manuset skrivs av folk som också spelar in det. Inget som inte går att filma." },
        ]}
        pricing={{
          heading: "Vad kostar manus och koncept?",
          intro: "Riktpriser exklusive moms, Sverige 2026.",
          rows: [
            { format: "Manus företagsfilm", scope: "Brief, kärnidé, scenlista, speakertext, 2 revideringar", price: "8 000–20 000 kr", lead: "1–2 veckor" },
            { format: "Reklamfilmsmanus", scope: "Koncept, 2–3 idéspår, manus, versionering per kanal", price: "25 000–75 000 kr", lead: "2–4 veckor" },
            { format: "Kampanjkoncept", scope: "Plattform, bärande idé, manus för flera filmer", price: "från 60 000 kr", lead: "3–5 veckor" },
            { format: "Storyboard", scope: "Bildmanus, 12–40 rutor, kamerarörelser", price: "6 000–35 000 kr", lead: "1–2 veckor" },
            { format: "Manus i abonnemang", scope: "Löpande manus och innehållsplan varje månad", price: "ingår från 14 995 kr/mån", lead: "löpande" },
          ],
          footnote: "Manus ingår alltid utan separat kostnad när vi också producerar filmen inom Pulse eller Prime.",
        }}
        process={{
          heading: "Så jobbar vi med manus och koncept",
          steps: [
            { label: "Brief och mål", body: "Vad ska filmen åstadkomma, för vem, i vilken kanal — och vad är det enda budskap som måste landa.", duration: "dag 1" },
            { label: "Kärnidé och koncept", body: "Vi presenterar 1–3 idéspår med hook, ton och bärande grepp. Ni väljer riktning.", duration: "vecka 1" },
            { label: "Struktur och scenlista", body: "Bild i vänsterkolumn, ljud i höger. Rytmen syns innan en enda mening är skriven.", duration: "vecka 1–2" },
            { label: "Manus och revideringar", body: "Speakertext eller dialog, tajmad mot längd. Två revideringsrundor ingår.", duration: "vecka 2" },
            { label: "Storyboard och produktionsplan", body: "Bildmanus, inspelningsplan, budget och tidplan — beslutsunderlag innan produktion.", duration: "vecka 2–3" },
          ],
        }}
        sections={[
          {
            id: "filmstrategi",
            heading: "Filmstrategi: planeringen ingen frågar efter men alla behöver",
            body: (
              <>
                <p>
                  De flesta filmprojekt kraschar inte i inspelningen utan i
                  planeringen: målet är oklart, alla i beslutskedjan har olika
                  bild av vad filmen ska göra, och manuset skrivs för att blidka
                  interna intressenter i stället för att övertyga en tittare.
                </p>
                <p>
                  Vår filmstrategi är avsiktligt kort: mål, målgrupp, kärnbudskap,
                  kanalmix, format och längd per kanal, samt en mätpunkt. Ett
                  dokument som får plats på två sidor och som fungerar som
                  facit när någon vill lägga till "en till grej" i manuset.
                </p>
              </>
            ),
          },
          {
            id: "konceptutveckling",
            heading: "Konceptutveckling — från budskap till bärande idé",
            body: (
              <p>
                Ett koncept är den kreativa mekanism som gör budskapet värt att
                titta på. Vi arbetar med 2–3 idéspår per uppdrag: ett tryggt, ett
                med tydlig kant och ett som utmanar kategorin. Alla tre prissätts
                och tidsätts, så att valet blir ett affärsbeslut och inte en
                smakfråga. Läs mer om{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/konceptutveckling-film">konceptutveckling för film</a>.
              </p>
            ),
          },
          {
            id: "delar",
            heading: "Delarna i klustret",
            body: (
              <>
                <p>
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/manusforfattare">Manusförfattare</a>{" "}
                  när ni behöver en skribent till en specifik film.{" "}
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilmsmanus">Reklamfilmsmanus</a>{" "}
                  när filmen ska sälja och versioneras per kanal.{" "}
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/storyboard">Storyboard</a>{" "}
                  när idén behöver visualiseras innan inspelning.
                </p>
                <p>
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/forproduktion">Förproduktion</a>{" "}
                  när planering, casting, tillstånd och budget ska sitta. Och{" "}
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/vad-kostar-ett-manus">vad ett manus kostar</a>{" "}
                  om ni först och främst vill veta prisbilden.
                </p>
              </>
            ),
          },
          {
            id: "leverans",
            heading: "Vad ni får",
            body: (
              <p>
                Ett strategidokument på max två sidor, ett konceptunderlag med
                valda idéspår, ett manus i tvåkolumnsformat med tajmning, och vid
                behov storyboard och produktionsplan. Allt i redigerbart format
                och fritt att använda — även om ni producerar filmen någon
                annanstans.
              </p>
            ),
          },
        ]}
        faq={FAQ}
        caseLimit={4}
        riskReducers
        closing={{
          title: "Har ni en film som behöver en idé?",
          body: "30 minuter där vi går igenom målet och skissar en riktning på manus och koncept.",
          primary: { label: "Boka strategimöte", to: "/kontakt" },
        }}
      />
      <RelatedKnowledgeAuto topics={["manus", "process", "reklamfilm", "strategi", "pris"]} />
    </>
  );
}
